import React, { useState, useEffect } from 'react';
import { View } from 'react-native';
import Typography from 'src/components/Text/Text';
import Button from 'src/components/Button/Button';
import IconInput from 'src/components/IconInput/IconInput';
import MapModal from 'src/components/MapModal/MapModal';
import { createStyles } from './CompanyAddressScreen.styles';
import map from 'src/assets/map.png';
import { useLinkTo } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/store';
import { updateCompany } from 'src/features/company/slice/companySlice';
import { Address } from 'src/types/common/address';
import { URLS } from 'src/constants/urls';
import { formatAddressToString } from 'src/helpers/addressHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getCompanyAddressTour } from 'src/features/tour/tourSteps';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { useDesktopLayout } from 'src/hooks/useDesktopLayout';

const CompanyAddressScreen: React.FC = () => {
  const linkTo = useLinkTo();
  const dispatch = useDispatch();
  const { screenWidth, contentMaxWidth } = useDesktopLayout();
  const effectiveWidth = contentMaxWidth ? Math.min(screenWidth, contentMaxWidth) : screenWidth;
  const styles = createStyles(effectiveWidth);

  // Redux selectors
  const company = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  // Local state for address fields
  const [formData, setFormData] = useState({
    garagingAddress: company?.garagingAddress as Address,
    mailingAddress: company?.mailingAddress as Address,
    isSameAddress:
      company?.garagingAddress.street === company?.mailingAddress.street ? 'yes' : 'no',
    twoYearsPreviousAuthority: company?.twoYearsPreviousAuthority ? true : false,
  });
  const [isMapVisible, setMapVisible] = useState(false);
  const [activeField, setActiveField] = useState<'garagingAddress' | 'mailingAddress' | null>(null);

  // Auto-start tour on first visit
  useTourAutoStart({
    tourKey: 'companyAddress',
    driverSteps: getCompanyAddressTour(),
    enabled: !!company,
  });

  useEffect(() => {
    // Trigger GTM event when the user lands on this page
    trackGTMEvent(
      'company-address-screen-visited',
      {
        screenName: 'CompanyAddressScreen',
        userEvent: 'User landed on company address screen',
      },
      accountType,
    );
  }, []);

  // Sync Redux state to form data
  useEffect(() => {
    if (company) {
      const mailingAddress =
        company?.mailingAddress.street && company?.mailingAddress.street.length > 0
          ? company?.mailingAddress
          : company?.garagingAddress;
      setFormData({
        garagingAddress: {
          ...company.garagingAddress,
          latitude: company.garagingAddress.latitude || 0,
          longitude: company.garagingAddress.longitude || 0,
        },
        mailingAddress: {
          ...mailingAddress,
          latitude: mailingAddress.latitude || 0,
          longitude: mailingAddress.longitude || 0,
        },
        isSameAddress:
          company.garagingAddress.street === company.mailingAddress.street ? 'yes' : 'no',
        twoYearsPreviousAuthority: company.twoYearsPreviousAuthority ? true : false,
      });
    }
  }, [company]);

  // Handle input changes (local updates only)
  const handleInputChange = (field: keyof typeof formData, value: string | boolean | Address) => {
    const updatedAddress =
      field === 'isSameAddress' && value === 'yes'
        ? { mailingAddress: formData.garagingAddress }
        : {};

    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...updatedAddress,
    }));
  };

  // Save address from the map modal
  const handleSaveAddress = (currentAddress: Address) => {
    if (!activeField) return;

    const shouldSyncMailingAddress =
      activeField === 'garagingAddress' && formData.isSameAddress === 'yes';

    setFormData((prev) => ({
      ...prev,
      [activeField]: currentAddress,
      ...(shouldSyncMailingAddress && { mailingAddress: currentAddress }),
    }));

    setActiveField(null);
    setMapVisible(false);
  };

  // Navigate back
  const navigateBack = () => {
    // Trigger GTM event when user clicks back to go to the company details edit page
    trackGTMEvent(
      'company-address-screen-left',
      {
        buttonTitle: 'Back',
        screenName: 'CompanyAddressScreen',
      },
      accountType,
    );
    linkTo(URLS.COMPANY.DETAILS);
  };

  // Save and continue
  const navigateNext = () => {
    // Save formData to Redux
    dispatch(updateCompany(formData));
    // Trigger GTM event after saving
    trackGTMEvent(
      'company-address-details-confirmed',
      {
        buttonTitle: 'Save & Continue',
        screenName: 'CompanyAddressScreen',
      },
      accountType,
    );
    // Navigate back to CompanyLandingScreen to review data
    linkTo(URLS.COMPANY.LANDING);
  };

  const isSaveDisabled = !(
    formData.garagingAddress.street.length > 0 && formData.mailingAddress.street.length > 0
  );

  return (
    <View style={styles.container}>
      <View style={styles.dataFieldsContainer}>
        <Typography variant="h2" style={styles.title}>
          Tell us what we missed!
        </Typography>

        {/* Same Address Buttons */}
        <TourWrapper tourId="same-address-question">
          <View style={styles.addressContainer}>
            <Typography variant="title2">
              Are your Garaging and Mailing Addresses the same?
            </Typography>
            <View style={styles.buttonGroup}>
              <Button
                testID={'same-garaging-n-mailing-address-yes'}
                type={formData.isSameAddress === 'yes' ? 'secondary' : 'tertiary'}
                title="Yes"
                style={styles.radioButton}
                onPress={() => {
                  handleInputChange('isSameAddress', 'yes');
                  handleInputChange('mailingAddress', formData.garagingAddress); // Update mailing address
                }}
              />
              <Button
                testID={'same-garaging-n-mailing-address-no'}
                type={formData.isSameAddress === 'no' ? 'secondary' : 'tertiary'}
                title="No"
                style={styles.radioButton}
                onPress={() => handleInputChange('isSameAddress', 'no')}
              />
            </View>
          </View>
        </TourWrapper>

        {/* Garaging Address */}
        <TourWrapper tourId="garaging-address-input">
          <View style={styles.garagingAddressContainer}>
            <IconInput
              testID={'garaging-address-selector'}
              label="Garaging Address"
              value={formatAddressToString(formData.garagingAddress)}
              placeholder="Enter garaging address"
              iconSource={map}
              iconWidth={20}
              iconHeight={20}
              onPress={() => {
                setActiveField('garagingAddress');
                setMapVisible(true);
              }}
            />
          </View>
        </TourWrapper>

        <TourWrapper tourId="mailing-address-input">
          <View style={styles.mailingAddressContainer}>
            <IconInput
              testID={'mailing-address-selector'}
              label="Mailing Address"
              value={formatAddressToString(formData.mailingAddress)}
              placeholder="Enter mailing address"
              iconSource={map}
              iconWidth={20}
              iconHeight={20}
              disabled={formData.isSameAddress === 'yes'} // Disable when "Yes" is selected
              onPress={() => {
                if (formData.isSameAddress === 'no') {
                  setActiveField('mailingAddress');
                  setMapVisible(true);
                }
              }}
            />
          </View>
        </TourWrapper>

        {/* Previous Experience */}
        <TourWrapper tourId="previous-authority-question">
          <View style={styles.prevExperianceContainer}>
            <Typography variant="title2">
              Do you have previous experience under another authority?
            </Typography>
            <View style={styles.buttonGroup}>
              <Button
                testID={'previous-experience-under-other-authority-yes'}
                type={formData.twoYearsPreviousAuthority === true ? 'secondary' : 'tertiary'}
                title="Yes"
                style={styles.radioButton}
                onPress={() => handleInputChange('twoYearsPreviousAuthority', true)}
              />
              <Button
                testID={'previous-experience-under-other-authority-no'}
                type={formData.twoYearsPreviousAuthority === false ? 'secondary' : 'tertiary'}
                title="No"
                style={styles.radioButton}
                onPress={() => handleInputChange('twoYearsPreviousAuthority', false)}
              />
            </View>
          </View>
        </TourWrapper>
      </View>

      {/* Navigation Buttons */}
      <View style={styles.buttonContainer}>
        <View style={styles.buttonWrapper1}>
          <Button type="tertiary" title="Back" onPress={navigateBack} />
        </View>
        <View style={styles.buttonWrapper2}>
          <TourWrapper tourId="save-continue-btn">
            <Button
              type="primary"
              colorVariant={isSaveDisabled ? 'disabled' : 'default'}
              title="Save & Continue"
              onPress={navigateNext}
              disabled={isSaveDisabled}
            />
          </TourWrapper>
        </View>
      </View>

      {/* Conditionally Render Map Modal */}
      {isMapVisible && (
        <MapModal
          testID={`company-address-${activeField}`}
          isVisible={isMapVisible}
          onClose={() => setMapVisible(false)}
          onSave={handleSaveAddress}
          title={activeField === 'garagingAddress' ? 'Add Garaging Address' : 'Add Mailing Address'}
          address={
            activeField === 'garagingAddress' ? formData.garagingAddress : formData.mailingAddress
          }
          initialPosition={{
            lat:
              activeField === 'garagingAddress'
                ? (formData.garagingAddress.latitude as number)
                : (formData.mailingAddress.latitude as number),
            lng:
              activeField === 'garagingAddress'
                ? (formData.garagingAddress.longitude as number)
                : (formData.mailingAddress.longitude as number),
          }}
        />
      )}
    </View>
  );
};

export default CompanyAddressScreen;
